<template>
  <section class="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-1">
    <!-- Summary: route label + ETA + tx count (shown before swap starts) -->
    <div
      v-if="showSummary"
      class="text-xs text-[#4e7397] pb-2 mb-1 border-b border-gray-100 space-y-1"
    >
      <div class="flex items-center justify-between">
        <span class="font-medium text-[#0e141b]">{{ routeLabel }}</span>
        <span>{{ txCount }} tx to sign</span>
      </div>
      <div class="flex items-center justify-between">
        <span>⏱ Est. {{ formattedEta }}</span>
      </div>
    </div>

    <div v-for="(step, index) in routeSteps" :key="step.key" class="flex gap-3">
      <!-- Left: indicator + line -->
      <div class="flex flex-col items-center">
        <!-- Circle -->
        <div
          class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors"
          :class="stepCircleClass(step.status)"
        >
          <!-- Pending: step number -->
          <span v-if="step.status === 'pending'">{{ index + 1 }}</span>
          <!-- Active: spinner -->
          <svg
            v-else-if="step.status === 'active'"
            class="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <!-- Completed: checkmark -->
          <svg
            v-else-if="step.status === 'completed'"
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <!-- Failed: X -->
          <svg
            v-else-if="step.status === 'failed'"
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <!-- Connecting line -->
        <div
          v-if="index < routeSteps.length - 1"
          class="w-0.5 flex-1 min-h-[16px] my-1 transition-colors"
          :class="step.status === 'completed' ? 'bg-[#16a34a]' : 'bg-gray-200'"
        />
      </div>

      <!-- Right: content -->
      <div class="pb-3 flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span
            class="text-sm font-medium"
            :class="step.status === 'pending' ? 'text-gray-500' : 'text-[#0e141b]'"
          >
            {{ step.label }}
          </span>
          <a
            v-for="tx in step.txLinks"
            :key="tx.url"
            :href="tx.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-[#1980e6] hover:text-[#4aa3ff] transition-colors"
          >
            {{ tx.label }}
          </a>
        </div>
        <!-- Amount line -->
        <div
          class="text-xs mt-0.5"
          :class="step.status === 'pending' ? 'text-gray-400' : 'text-gray-600'"
        >
          <template v-if="step.amountLoading">
            <span class="inline-block w-20 h-3 bg-gray-200 rounded animate-pulse" />
          </template>
          <template v-else>
            {{ step.amountLine }}
          </template>
        </div>
        <!-- ETA hint when bridge is pending or active -->
        <div
          v-if="(step.status === 'active' || step.status === 'pending') && step.etaHint"
          class="text-xs mt-1 text-[#4e7397] italic"
        >
          {{ step.etaHint }}
        </div>
      </div>
    </div>

    <!-- Bottom: expected / received SPOTTY -->
    <div
      v-if="bottomLine"
      class="pt-2 mt-1 border-t border-gray-200 flex items-center justify-between"
    >
      <span class="text-sm" :class="isSuccess ? 'text-[#16a34a]' : 'text-gray-500'">
        {{ bottomLine.label }}
      </span>
      <span class="text-sm font-semibold" :class="isSuccess ? 'text-[#16a34a]' : 'text-[#0e141b]'">
        {{ bottomLine.value }}
      </span>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  useRouteStepperPresenter,
  type RouteStepperProps,
} from './route-stepper/useRouteStepperPresenter'

const props = defineProps<RouteStepperProps>()
const {
  bottomLine,
  formattedEta,
  isSuccess,
  routeLabel,
  routeSteps,
  showSummary,
  stepCircleClass,
  txCount,
} = useRouteStepperPresenter(props)
</script>
